import { AlertTriangle, Bell, CheckCheck, CheckCircle2, Info, XCircle } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { apiClient, type AppNotification } from "@/shared/api/api-client";
import { AppLink } from "@/shared/components/AppLink";
import { ApiError } from "@/shared/api/http";
import { useDismiss } from "@/shared/hooks/useDismiss";
import { usePolling } from "@/shared/hooks/usePolling";
import {
  NOTIFICATIONS_CHANGED_EVENT,
  notificationLink,
  relativeTime
} from "@/shell/notifications";

/** Cadência do sino. Bem acima da das telas de job (2,5 s): aqui ninguém
 *  está esperando um número andar, e o sino vive em TODA tela — com a
 *  cadência delas, cada aba aberta faria uma requisição a cada 2,5 s pra
 *  sempre. O backoff do `usePolling` ainda sobe pra 60 s e 120 s quando
 *  nada muda, e a aba escondida não pergunta nada. */
const BELL_INTERVAL_MS = 30_000;

const LEVEL_ICON = {
  success: CheckCircle2,
  warning: AlertTriangle,
  error: XCircle,
  info: Info
} as const;

export function NotificationBell({ token }: { token: string }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<AppNotification[]>([]);
  const [unread, setUnread] = useState(0);
  const [failed, setFailed] = useState(false);
  const [pausedUntil, setPausedUntil] = useState<number | null>(null);
  const controlRef = useRef<HTMLDivElement>(null);

  useDismiss(open, () => setOpen(false), { ref: controlRef });

  const load = useCallback(async () => {
    try {
      const list = await apiClient.listNotifications(token);
      setItems(list.items);
      setUnread(list.unread_count);
      setFailed(false);
    } catch (failure) {
      if (failure instanceof ApiError && failure.retryAfterSeconds) {
        setPausedUntil(Date.now() + failure.retryAfterSeconds * 1000);
      }
      setFailed(true);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const reload = () => void load();
    window.addEventListener(NOTIFICATIONS_CHANGED_EVENT, reload);
    return () => window.removeEventListener(NOTIFICATIONS_CHANGED_EVENT, reload);
  }, [load]);

  usePolling({
    enabled: true,
    onTick: () => void load(),
    signature: `${unread}:${items[0]?.id ?? 0}`,
    pausedUntil,
    intervalMs: BELL_INTERVAL_MS
  });

  const toggle = () => {
    setOpen(current => !current);
    // Abrir é o momento em que a pessoa quer o estado de agora, não o
    // de até 30 s atrás.
    if (!open) void load();
  };

  const markRead = async (notification: AppNotification) => {
    if (notification.read) return;
    // Otimista: o clique já levou a pessoa pra outra tela, e o sino não
    // pode continuar acusando o que ela acabou de abrir.
    setItems(current =>
      current.map(item => (item.id === notification.id ? { ...item, read: true } : item))
    );
    setUnread(current => Math.max(0, current - 1));
    try {
      await apiClient.markNotificationRead(token, notification.id);
    } catch {
      void load();
    }
  };

  // Marca como lida e fecha o painel. Quem NAVEGA, quando o aviso tem
  // destino, é o `<a>` do item (2026-10-26): assim vale clique do meio e
  // "abrir em nova aba"; sem destino o item continua sendo um botão.
  const openNotification = (notification: AppNotification, hasLink: boolean) => {
    void markRead(notification);
    if (hasLink) setOpen(false);
  };

  const markAll = async () => {
    setItems(current => current.map(item => ({ ...item, read: true })));
    setUnread(0);
    try {
      await apiClient.markAllNotificationsRead(token);
    } catch {
      void load();
    }
  };

  const label = unread > 0 ? `Notificações: ${unread} não lidas` : "Notificações";

  return (
    <div className="notification-control" ref={controlRef}>
      <button
        className="icon-button notification"
        aria-label={label}
        title={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={toggle}
      >
        <Bell size={18}/>
        {unread > 0 && (
          <span className="notification-badge" aria-hidden="true">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="notification-panel" role="dialog" aria-label="Notificações">
          <header className="notification-panel-head">
            <strong>Notificações</strong>
            {unread > 0 && (
              <button type="button" className="notification-mark-all" onClick={() => void markAll()}>
                <CheckCheck size={14}/>
                Marcar todas como lidas
              </button>
            )}
          </header>
          {failed && items.length === 0 ? (
            <p className="notification-empty">Não foi possível carregar as notificações.</p>
          ) : items.length === 0 ? (
            <p className="notification-empty">
              Nada por aqui. Avisaremos quando um upload, amostra ou outro
              processo em segundo plano terminar.
            </p>
          ) : (
            <ul className="notification-list">
              {items.map(notification => {
                const Icon = LEVEL_ICON[notification.level] ?? Info;
                const link = notificationLink(notification);
                const itemClass = `notification-item ${notification.read ? "" : "is-unread"}`;
                const itemBody = (
                  <>
                  <Icon className={`notification-icon level-${notification.level}`} size={18}/>
                  <span className="notification-text">
                    <span className="notification-title">{notification.title}</span>
                    {notification.body && (
                      <span className="notification-body">{notification.body}</span>
                    )}
                    <span className="notification-meta">
                      {notification.company_name && <>{notification.company_name} · </>}
                      {/* Quem mudou a configuração (RN-18, 2026-10-22). */}
                      {notification.actor_name && <>por {notification.actor_name} · </>}
                      <time dateTime={notification.created_at}>
                        {relativeTime(notification.created_at)}
                      </time>
                    </span>
                  </span>
                  {!notification.read && <span className="notification-dot" aria-label="Não lida"/>}
                  </>
                );
                return (
                  <li key={notification.id}>
                    {link ? (
                      <AppLink
                        className={itemClass}
                        to={link}
                        onClick={() => openNotification(notification, true)}
                      >
                        {itemBody}
                      </AppLink>
                    ) : (
                      <button
                        type="button"
                        className={itemClass}
                        onClick={() => openNotification(notification, false)}
                      >
                        {itemBody}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
